/**
 * App bootstrap
 *
 * Registers the custom elements, restores settings, mounts <app-shell>, starts
 * the router, and registers the service worker. Nothing computes here.
 *
 * ⚠ **Order matters.** Settings are restored and the theme applied *before* the
 * first render, or the page flashes light and then repaints dark. And the
 * component modules are imported for their side effect of calling
 * `customElements.define`, which must happen before <app-shell> is upgraded.
 *
 * © Manish Jagdish Thatte
 */

// ◆ One stylesheet import. main.css @imports the layers in order — tokens,
// reset, components, chart, print — so a component rule can never win over a
// reset by accident of import order here.
import './styles/main.css';

import { restoreSettings, getSettings, setStorage, setStatus, subscribe } from './store.js';
import { applyDocumentLocale, loadLocale } from './utils/i18n.js';
import { available } from './utils/db.js';
import { start } from './router.js';
import { startPwa } from './utils/pwa.js';

// Registered for their side effects.
import './components/app-shell.js';
import './components/site-nav.js';
import { applyTheme } from './components/theme-toggle.js';
import './components/locale-switcher.js';
import './components/disclaimer-note.js';
import './components/chart-north.js';
import './components/chart-south.js';
import './components/chart-east.js';
import './components/rashi-wheel.js';
import './components/planet-table.js';
import './components/input-datetime.js';
import './components/place-picker.js';
import './components/panchang-card.js';
import './components/varga-grid.js';
import './components/dasha-tree.js';
import './components/dasha-timeline.js';
import './components/ashtakavarga-grid.js';
import './components/guna-table.js';
import './components/palm-canvas.js';
import './components/palm-overlay-legend.js';

async function boot() {
  // 1. Settings first, so the theme is right on the first paint.
  const { dropped } = restoreSettings();
  const settings = getSettings();
  applyTheme(settings.theme);

  // 2. Locale, before any component renders a label.
  //
  // ◆ Components already upgraded during the imports above and rendered against
  // an empty bundle. `loadLocale` notifies its own listeners once the data is
  // in, which is what makes them repaint with real text.
  await loadLocale(settings.locale);
  applyDocumentLocale(settings.locale);

  // Switching language has to load the new bundle BEFORE anything re-renders,
  // or the nav paints raw keys again for one frame.
  subscribe('settings.locale', async (locale) => {
    await loadLocale(locale);
    applyDocumentLocale(locale);
    // Pages hardcode less when they use `t()`, but they still need a fresh render
    // so English/Hindi/Marathi/Sanskrit switch is visible on the open page.
    const { route } = await import('./router.js');
    await route();
  });

  subscribe('settings.theme', (theme) => applyTheme(theme));

  // 3. Tell the user what storage they actually have, before they enter
  //    ten minutes of birth data that might not be savable.
  const storage = await available();
  setStorage(storage);
  if (!storage.persistent) setStatus(storage.reason, 'warning');

  // ⚠ A dropped setting means a stored preference was invalid — most often
  // because a value this project removed (true Revatī) is still in localStorage.
  // Say so rather than silently reverting to a default the user did not choose.
  if (dropped.length) {
    setStatus(
      `Some saved preferences were not recognised and have been reset: ` +
      dropped.map((d) => d.key).join(', ') + '.',
      'warning'
    );
  }

  // Session restore (cookie). Failure must not block the free site.
  try {
    const { refreshSession } = await import('./utils/auth-client.js');
    await refreshSession();
  } catch (err) {
    console.warn('[main] auth session refresh skipped:', err);
  }

  const mount = document.querySelector('#main');
  if (!mount) throw new Error('main.js: no #main element to mount into');
  await start(mount);

  // 4. The service worker last: it must never delay the first render, and a
  //    failure to register is not a reason to break the page.
  let registration = null;
  if ('serviceWorker' in navigator && import.meta.env?.PROD) {
    try {
      registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    } catch (err) {
      console.warn('[main] service worker did not register:', err);
    }
  }
  // The offline / update / install notices (src/utils/pwa.js). Never reloads on its own.
  startPwa(registration);
}

boot().catch((err) => {
  // ⚠ Last resort. A thrown boot leaves the noscript message showing, which
  // wrongly tells the user JavaScript is off. Replace it with the truth.
  console.error('[main] boot failed:', err);
  const mount = document.querySelector('#main');
  if (mount) {
    mount.textContent = '';
    const h = document.createElement('h1');
    h.textContent = 'shastra.site could not start';
    const p = document.createElement('p');
    p.textContent =
      'Something failed while loading. Reloading usually fixes it. If it does not, ' +
      'the details below say what went wrong.';
    const pre = document.createElement('pre');
    pre.className = 'field-note';
    pre.textContent = String(err?.stack ?? err);
    mount.append(h, p, pre);
  }
});
